import { getAscensionStatMultiplier } from '../../data/ascensions.js';

// Sovereign Shards: a second currency, separate from Echoes, awarded on
// every boss kill (never on a loss) — funds pet summons (ShopSummonMenu,
// 10/single, 100/10-roll) and one-time perks (SHARD_UNLOCKS, 20-40 each,
// 280 total) rather than the incremental stat upgrades Echoes buys. Scales
// per boss, same shape as ECHO_BONUS_PER_BOSS below, indexed by
// gauntletIndex — one entry per BOSSES slot (seven since the 2026-07
// gauntlet expansion). The last index (The Unmade King, the true final
// boss) jumps further than any prior step — its kill is meant to feel like
// the run's actual climax, not just one more increment.
//
// 2026-07-20 balance pass: doubled from the original [1,1,2,2,3,3,5] (17/
// full clear) — that total left every perk (280 Shards combined) at ~16
// clears away, more than double Echoes' ~7-10 clears to max every meta
// upgrade, and didn't even account for the pet-gacha sink competing for the
// same currency. 34/clear puts King's Ransom (the cheapest accelerant, 40
// Shards) within a single clear, and the full 280-Shard perk set within the
// same ~8-clear ballpark as Echoes' meta progression, same doubling move
// Echoes' own per-boss table got in an earlier pass (see ECHO_BONUS_PER_BOSS).
//
// 2026-07-27 act-rate pass: [2,2,4,4,6,6,10] (34/clear) -> 42, for the same
// reason ECHO_BONUS_PER_BOSS moved below.
//
// The King's own payout is capped at 12 rather than lifted with the rest,
// because act2Bosses.test.js holds a rail this must not break: the first boss
// of Act II pays at least as much as the last boss of Act I, so the reward
// ladder never dips at an act boundary. ACT2_SHARDS opens at 12. The raise
// therefore goes into the six bosses in front of the King, which is where a
// new player spends their time anyway.
export const SHARDS_PER_BOSS = [3, 3, 5, 5, 7, 7, 12];

// Flat Echo bonus banked immediately on each boss's real death (see
// Game.js's _onBossDied), on top of the cumulative calcEchoes() payout at
// true run end — indexed by gauntletIndex, escalating with the bosses.
// The founding four's doubled values ([80, 110, 140, 200], up from the
// original [40, 55, 70, 100]) kept their places; the interleaved bosses
// interpolate between their neighbors.
//
// 2026-07-27 act-rate pass: [80,95,110,125,140,165,200] (915/clear) -> 1647.
//
// The measurement that forced it, from pacingBench's seed panel:
//
//              boss payout   clear time   echoes/min
//   Act I           915        9.7 min          94
//   Act II         2220        5.0 min         444
//   Act III        2280        4.4 min         518
//
// Act I paid **a fifth of the rate** of the acts after it, for twice the
// time — and it is the only act a new player can reach. That is not
// progression, it is a cliff at the exact point a player is deciding whether
// the game is worth another session. Later acts should pay better; they
// should not pay 5x better per minute.
//
// Paired with Act I's boss HP coming down 20% (data/bosses.js), which is the
// other half of the same ratio: this lifts the numerator, that cuts the
// denominator. Together Act I goes from 94 to ~141 echoes/min — a 50% raise
// against a 9.7 -> 8.1 minute clear.
//
// **Why not further.** act2Bosses.test.js holds the ladder continuous across
// act boundaries: Act II's first boss must pay at least as much as Act I's
// last. ACT2_ECHO_BONUS opens at 260, so the King is capped at 250 and the
// whole curve is scaled to land there. Closing the rate gap the rest of the
// way would mean lifting Act II and Act III too, which is a different and
// much larger change to an economy those two acts were deliberately
// rate-matched against each other in — not something to do as a side effect
// of fixing the on-ramp. The residual gap is ~3x rather than 5.5x, and it now
// points the right way for the right reason: later acts pay better because
// they are later, not because the first one was underwater.
export const ECHO_BONUS_PER_BOSS = [100, 119, 138, 156, 175, 206, 250];

// Endless uses the same 25% exponential loop ramp as boss HP and wave HP.
// Keeping this in the currency module makes the reward curve explicit and
// prevents normal runs from accidentally inheriting Endless scaling.
export function getEndlessEchoMultiplier(loop = 0) {
  return Math.pow(1.25, Math.max(0, loop));
}

/**
 * Ascension rewards scale linearly at +20% per level (A3 is 1.6x, not
 * 1.2^3), capping at 3x at Ascension X — deliberately mirroring the +20%/
 * level enemy-stat climb (see data/ascensions.js) so payout tracks the
 * difficulty it costs.
 */
export function getAscensionRewardMultiplier(ascensionLevel = 0) {
  return 1 + Math.max(0, ascensionLevel) / 5;
}

// Damage credit is capped at a full boss HP bar's worth so overkill hits (or,
// pre-fix, an infinite-tick weapon like Orbit Ward) can't inflate payout past
// what a clean kill already grants.
const DAMAGE_CREDIT_MAX = 100;

// Survival credit is a floor for a loss, not a farmable income: capped well
// below DAMAGE_CREDIT_MAX so stalling forever without landing hits never
// outpays actually killing the boss.
const SURVIVAL_CREDIT_PER_SECOND = 0.3;
const SURVIVAL_CREDIT_MAX = 45;

// Minion damage pays Echoes too (same spirit as XP — see ProgressionSystem),
// but as its own flat-rate, capped credit rather than folded into
// damageCredit's ratio: a wave stage's total minion HP is unrelated to the
// current boss's max HP, so mixing the two would distort a formula that's
// deliberately boss-HP-relative. Capped well below DAMAGE_CREDIT_MAX for the
// same reason survival credit is — clearing minions can't outpay actually
// fighting the boss.
const MINION_DAMAGE_CREDIT_RATE = 0.03;
const MINION_DAMAGE_CREDIT_MAX = 40;

/**
 * Run currency ("Echoes"), per ARCHIVE.md: scales with damage dealt
 * (relative to boss max HP, so it's comparable across bosses with very
 * different HP pools) plus minion damage and a capped survival-time floor
 * so even a loss pays out something, without stalling being competitive
 * with winning.
 */
export function calcEchoes({ totalDamageDealt, bossMaxHp, survivalSeconds, minionDamageDealt = 0 }) {
  const damageCredit = Math.min(totalDamageDealt / bossMaxHp, 1) * DAMAGE_CREDIT_MAX;
  const minionDamageCredit = Math.min(minionDamageDealt * MINION_DAMAGE_CREDIT_RATE, MINION_DAMAGE_CREDIT_MAX);
  const survivalCredit = Math.min(survivalSeconds * SURVIVAL_CREDIT_PER_SECOND, SURVIVAL_CREDIT_MAX);
  return Math.floor(damageCredit + minionDamageCredit + survivalCredit);
}

// Glimmer: the cosmetic currency (the third, after Echoes and Shards) — spent
// only on vanity (data/cosmetics.js, the Wardrobe), never combat, so it's
// safe to be generous and it never needs to sit in the same balance
// conversation as the other two. Earned from how far you got and how
// stylishly you played: grazes (near-misses) are a "flair" stat that had no
// payout attached before, which is exactly the flavor a vanity currency
// wants ("reward showing off"). Every boss cleared pays out; a win tops it.
const GLIMMER_PER_BOSS = 6;
const GLIMMER_PER_GRAZE = 0.15;
const GLIMMER_WIN_BONUS = 20;

/**
 * Cosmetic-currency payout for a finished run. `gauntletIndex` is how many
 * boss slots were passed (0-based); a win adds one more for the boss just
 * killed. Not granted for Boss Challenge runs (Game._endRun early-returns
 * before this, same as Echoes/Shards).
 */
export function calcGlimmer({ gauntletIndex, grazes = 0, won = false, captureGlimmer = 0, grazeValue = null }) {
  const bossesThisRun = Math.max(0, gauntletIndex) + (won ? 1 : 0);
  // `grazeValue` is the chain-weighted graze total (GrazeChain.value,
  // BULLET-HELL.md §D5): the same graze count is worth more when the grazes
  // were chained, which is precisely the flair this currency is for. Falls
  // back to the raw count when no chain is wired in.
  const credited = grazeValue == null ? grazes : grazeValue;
  return Math.floor(
    bossesThisRun * GLIMMER_PER_BOSS
    + Math.max(0, credited) * GLIMMER_PER_GRAZE
    // Spell-card captures (BULLET-HELL.md §D1) bank their own Glimmer as they
    // happen (CaptureTracker prices each card); this is just where the total
    // is paid. Vanity is the right currency for it: capture is a flair stat
    // like graze, and pricing skill in a combat currency would mean the
    // players who need the help least get the most of it.
    + Math.max(0, captureGlimmer)
    + (won ? GLIMMER_WIN_BONUS : 0),
  );
}

// ---------------------------------------------------------------------------
// Leaderboard score (2026-07 rework)
//
// Shape: (progress + performance) * difficulty * purity.
//
// The old formula was a flat sum of six hand-tuned constants, which meant every
// balance pass silently re-weighted the ladder: ASCENSION_SCORE_BONUS had grown
// to 50k/level (an A10 run was worth FIFTY gauntlet steps), and the uncapped
// `damageCredit` term — roughly equal to gauntlet credit on a full clear, and
// growing 1.2^loop in Endless — inverted the documented ranking hierarchy at
// exactly the depth where runs get competitive.
//
// Progress is the spine and always dominates. Performance is a within-run
// tiebreak, deliberately capped below BOSS_POINTS so it can never leapfrog a
// boss. Difficulty and purity are MULTIPLIERS, so they scale a run's worth
// proportionally instead of bolting on a constant that drifts out of step with
// the rest of the game.
//
// The damage term is gone entirely: advancing a step already requires dealing
// that boss's full HP, so it only ever double-counted progress.
// ---------------------------------------------------------------------------

// Per boss actually killed. The spine of the score — every other additive term
// is kept below this so progress can never be leapfrogged by performance.
//
// Scale note: every additive term below was divided by 4 together (2026-07-21),
// dropping deep-Endless totals out of the millions and back to six figures.
// Because the whole additive side moved by one shared factor and the
// multipliers didn't move at all, every ranking is bit-for-bit identical to
// before — only the printed magnitude changed. Keep it that way: rescaling one
// of these constants on its own silently re-weights the ladder, which is the
// exact failure mode this formula was rewritten to avoid.
const BOSS_POINTS = 5000;

// Win-side performance pool, paid out on a curve from an act's par down to 0.
// A clear at par earns nothing here; the pool is the reward for beating par.
const SPEED_POOL = 7000;

// Every act is a standalone run of its own now (data/acts.js), and each scores
// on its own board, so each needs its own clear bonus and its own par. Scoring
// a seven-boss Act I against the constants that used to price a thirteen-boss
// campaign would hand every clear the full speed pool — the run finishes ~15
// minutes inside the old par, so the term would stop discriminating entirely.
//
// The three are the same shape deliberately: each board's ladder is then
// proportioned identically within itself, which is the whole point of giving
// the acts separate boards rather than separate formulas.
//
// The clear bonus is granted on a win OR on any Endless run. Endless can end
// EITHER way: Game._onBossDied's loop-boundary check calls _endRun(true) and
// banks a real win, while dying mid-loop ends the same run with won=false.
// Since one runId upserts a single row, that death overwrites the earlier win —
// the `endless` half of that condition is what keeps the clear already earned.
//
// Act I (7 bosses) is the default because it is the run every player starts on
// and the one Endless loops.
export const ACT1_CLEAR_BONUS = 24000;
export const ACT1_PAR_SECONDS = 1130; // ~19 min — the target pace for a seven-boss Act I clear

// Act II (ACT2-BIBLE §1) and Act III (ACT3-BIBLE §9) are both six-boss acts, so
// they price identically. They are harder fights than Act I's, which the boss
// credit and the Ascension multiplier already pay for; par is about pace.
export const ACT2_CLEAR_BONUS = 21000;
export const ACT2_PAR_SECONDS = 1000; // ~17 min — the target pace for a six-boss act clear
export const ACT3_CLEAR_BONUS = 21000;
export const ACT3_PAR_SECONDS = 1000;

// The Undersong Tide (BULLET-HEAVEN.md §H7) keeps the constants the campaign
// used before the acts were split apart. Not because they describe the Tide
// well — it is a 20-minute clock, not a march to a par time — but because it
// has its own board and rows already on it, and re-pricing the clear bonus
// would leave every existing row 21,000 points out of reach.
export const TIDE_CLEAR_BONUS = 45000;
export const TIDE_PAR_SECONDS = 2100;

// Loss-side performance: how long you held on, per second, capped by
// MAX_RUN_SECONDS below (3600 * 1.25 = 4500, still under BOSS_POINTS).
const LOSS_TIME_RATE = 1.25;

// A generous ceiling (45 min) for the slowest sane full clear of the
// seven-boss gauntlet. Caps the loss-side survival credit so an extreme stall
// can't inflate score unboundedly.
const MAX_RUN_SECONDS = 3600;

// Per completed Endless loop, added to the difficulty multiplier. Modest on
// purpose: depth is already paid linearly through BOSS_POINTS, so this only
// prices the fact that each loop's enemies are meaningfully harder.
const ENDLESS_LOOP_BONUS = 0.15;

// Purity — the per-run opt-outs of permanent progression (Game.js's
// disableEchoUpgrades / disableShardPerks / disablePetBonuses). These existed
// specifically "for a fair leaderboard comparison" but were never recorded, so
// an unaided run and a fully-buffed veteran run posted identical rows. As
// multipliers they finally make the toggles mean something.
const PURITY_NO_ECHO_UPGRADES = 0.20;
const PURITY_NO_SHARD_PERKS = 0.15;
const PURITY_NO_PET_BONUSES = 0.15;

export function calcLeaderboardScore(args) {
  return calcLeaderboardScoreBreakdown(args).total;
}

/**
 * @param {number} gauntletIndex   bosses killed so far (a win doesn't increment
 *                                 it, hence the +1 below); keeps climbing in Endless
 * @param {number} endlessLoop     completed Endless loops — floor(gauntletIndex /
 *                                 BOSSES.length), computed by the caller so this
 *                                 module stays free of data/ imports
 * @param {boolean} noEchoUpgrades disableEchoUpgrades was set for the run
 * @param {boolean} noShardPerks   disableShardPerks was set for the run
 * @param {boolean} noPetBonuses   disablePetBonuses was set for the run
 * @param {number} startIndex      the roster index the run BEGAN at. 0 for Act I
 *   and Endless; each later act's own start, so its progress term counts bosses
 *   cleared within that act instead of crediting a climb it never fought. That
 *   mismatch is exactly why every act scores on its own board — data/acts.js.
 * @param {number} clearBonus      the act's clear bonus (ACT1_/ACT2_/ACT3_CLEAR_BONUS)
 * @param {number} parSeconds      the act's speed curve (ACT1_/ACT2_/ACT3_PAR_SECONDS)
 */
export function calcLeaderboardScoreBreakdown({
  gauntletIndex,
  survivalSeconds = 0,
  won = false,
  ascensionLevel = 0,
  endless = false,
  endlessLoop = 0,
  noEchoUpgrades = false,
  noShardPerks = false,
  noPetBonuses = false,
  startIndex = 0,
  clearBonus: clearBonusValue = ACT1_CLEAR_BONUS,
  parSeconds = ACT1_PAR_SECONDS,
}) {
  // A win ends the run WITHOUT incrementing gauntletIndex, so the final boss
  // went uncounted — a full clear used to score as six kills, not seven.
  const bossesCleared = Math.max(0, Math.floor((gauntletIndex || 0) - startIndex)) + (won ? 1 : 0);
  const bossCredit = bossesCleared * BOSS_POINTS;
  const clearBonus = (won || endless) ? clearBonusValue : 0;

  // Speed is only meaningful where there's a finish line. The gauntlet has one,
  // so a clear is scored on how fast it was. Endless has none — it's scored on
  // endurance whether the run ended on a loop-boundary WIN or a mid-loop death,
  // because "finished quickly" is not an achievement in a mode with no end.
  // (Paying speed on an Endless win used to award exactly 0: those runs always
  // exceed PAR_SECONDS, so the curve clamped to zero.)
  const scoredOnSpeed = won && !endless;
  const performanceCredit = scoredOnSpeed
    ? Math.round(SPEED_POOL * Math.max(0, 1 - Math.max(0, survivalSeconds) / parSeconds))
    : Math.round(Math.min(Math.max(0, survivalSeconds), MAX_RUN_SECONDS) * LOSS_TIME_RATE);

  const subtotal = bossCredit + clearBonus + performanceCredit;

  // Grounded in the real enemy-stat curve (data/ascensions.js) rather than a
  // magic constant, so a future balance change to Ascension can't desync the
  // ladder from the difficulty it's supposed to be pricing.
  const ascensionMultiplier = getAscensionStatMultiplier(ascensionLevel);
  const endlessMultiplier = 1 + ENDLESS_LOOP_BONUS * Math.max(0, Math.floor(endlessLoop || 0));
  const difficultyMultiplier = ascensionMultiplier * endlessMultiplier;

  const purityMultiplier = 1
    + (noEchoUpgrades ? PURITY_NO_ECHO_UPGRADES : 0)
    + (noShardPerks ? PURITY_NO_SHARD_PERKS : 0)
    + (noPetBonuses ? PURITY_NO_PET_BONUSES : 0);

  const total = Math.round(subtotal * difficultyMultiplier * purityMultiplier);

  return {
    total,
    won,
    bossesCleared,
    bossCredit,
    clearBonus,
    performanceCredit,
    // Which metric performanceCredit was scored on, so callers label it
    // correctly instead of re-deriving the won/endless rule.
    performanceKind: scoredOnSpeed ? 'speed' : 'survival',
    subtotal,
    ascensionMultiplier,
    endlessMultiplier,
    difficultyMultiplier,
    purityMultiplier,
  };
}
